import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, Plus, Loader2, Check, Trash2, Flag, ChevronDown } from "lucide-react";
import { listExamPlans, createExamPlan, toggleExamTask, deleteExamPlan, listLectures } from "@/lib/api";
import { toast } from "sonner";

const daysUntil = (dateStr) => {
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
};

export const ExamCountdown = () => {
  const [plans, setPlans] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [examDate, setExamDate] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openPlan, setOpenPlan] = useState(null);

  const refresh = () => listExamPlans().then(setPlans).catch(() => setPlans([]));

  useEffect(() => {
    refresh();
    listLectures().then(setLectures).catch(() => {});
  }, []);

  const toggleLec = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    if (!title.trim() || !examDate || selected.length === 0) {
      toast.error("Add a title, exam date, and pick at least one lecture.");
      return;
    }
    setBusy(true);
    try {
      const plan = await createExamPlan({ title: title.trim(), exam_date: examDate, lecture_ids: selected });
      setPlans((p) => [plan, ...(p || [])]);
      setOpenPlan(plan.id);
      setCreating(false);
      setTitle(""); setExamDate(""); setSelected([]);
      toast.success("Study plan created!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create plan");
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = async (plan, index) => {
    const done = !plan.tasks[index].done;
    const updated = await toggleExamTask(plan.id, index, done).catch(() => null);
    if (updated) setPlans((ps) => ps.map((p) => (p.id === plan.id ? updated : p)));
  };

  const remove = async (id) => {
    await deleteExamPlan(id).catch(() => {});
    setPlans((ps) => ps.filter((p) => p.id !== id));
  };

  if (plans === null) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Exam Countdown</span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">Beat the clock</h1>
          <p className="mt-1 text-muted-foreground">Set an exam date and we'll build a day-by-day plan from your chapters.</p>
        </div>
        <button
          data-testid="exam-new-btn"
          onClick={() => setCreating((c) => !c)}
          className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
        >
          <Plus className="h-4 w-4" /> New Plan
        </button>
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-8"
          >
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Exam name</label>
                  <input
                    data-testid="exam-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Biology Midterm"
                    className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Exam date</label>
                  <input
                    data-testid="exam-date"
                    type="date"
                    value={examDate}
                    onChange={(e) => setExamDate(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Chapters to study</label>
                {lectures.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">Create some lectures first, then build a plan around them.</p>
                ) : (
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto custom-scroll">
                    {lectures.map((l) => (
                      <button
                        key={l.id}
                        data-testid={`exam-lecture-${l.id}`}
                        onClick={() => toggleLec(l.id)}
                        className={`text-left rounded-xl border-2 px-4 py-2.5 text-sm transition ${
                          selected.includes(l.id) ? "border-accent bg-accent/10" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <span className="font-medium line-clamp-1">{l.title}</span>
                        <span className="block text-xs text-muted-foreground line-clamp-1">{l.book_title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                data-testid="exam-create-btn"
                onClick={create}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3 font-semibold disabled:opacity-50 hover:opacity-90 transition"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                Build my study plan
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {plans.length === 0 && !creating && (
        <div className="rounded-2xl border border-dashed border-border py-20 text-center">
          <CalendarClock className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-4 text-muted-foreground">No exam plans yet — create one to get a countdown and daily tasks.</p>
        </div>
      )}

      <div className="space-y-5">
        {plans.map((plan) => {
          const left = daysUntil(plan.exam_date);
          const done = plan.tasks.filter((t) => t.done).length;
          const pct = Math.round((done / plan.tasks.length) * 100);
          const isOpen = openPlan === plan.id;
          return (
            <div key={plan.id} data-testid="exam-plan-card" className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-2xl font-semibold">{plan.title}</h3>
                    <p className="text-sm text-muted-foreground">Exam on {new Date(plan.exam_date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-serif text-4xl font-bold ${left <= 3 ? "text-destructive" : "text-accent"}`}>{left > 0 ? left : 0}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">days left</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-sm font-mono text-muted-foreground">{done}/{plan.tasks.length}</span>
                  <button onClick={() => remove(plan.id)} className="grid place-items-center h-8 w-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`exam-toggle-${plan.id}`}
                    onClick={() => setOpenPlan(isOpen ? null : plan.id)}
                    className="grid place-items-center h-8 w-8 rounded-full border border-border hover:border-primary/50 transition"
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden border-t border-border">
                    <div className="p-6 space-y-2">
                      {plan.tasks.map((t, i) => (
                        <button
                          key={i}
                          data-testid={`exam-task-${i}`}
                          onClick={() => toggleTask(plan, i)}
                          className="w-full flex items-center gap-3 rounded-xl border border-border px-4 py-3 text-left hover:border-accent/50 transition"
                        >
                          <span className={`grid place-items-center h-6 w-6 rounded-md shrink-0 ${t.done ? "bg-accent text-accent-foreground" : "border-2 border-border"}`}>
                            {t.done ? <Check className="h-4 w-4" /> : t.lecture_id === null ? <Flag className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                          </span>
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.activity}</p>
                            <p className="text-xs text-muted-foreground">Day {t.day} · {new Date(t.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })} {t.title && t.lecture_id ? `· ${t.title}` : ""}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
